import { DateTime } from 'infras/datetime';
import { useContext, useMemo, SyntheticEvent } from 'react';
import { useEvent } from '../hooks/useEvent';
import { useSlot } from '../hooks/useSlot';
import {
  CalendarMode,
  CalendarWeekDay,
  Frame,
  MergeableEventListItem,
  Schedule,
  SlotViewType,
} from 'presentations/views/components/atoms/Calendar/reducer';
import { CalendarContext } from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import { isMobileApp } from 'presentations/views/helpers/electron';
import { imagePath } from 'presentations/assets';

interface Props {
  startedAt: DateTime;
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
}

export function useDayTimeGrid(props: Props) {
  const { state, dispatch } = useContext(CalendarContext);

  const dates = useMemo(() => {
    const items: DateTime[] = [];
    if (state.showRange.day) {
      for (let i = 0; i < state.showRange.day; i++) {
        const date = props.startedAt.plus({ day: i });
        const weekday = (date.weekday % 7) as CalendarWeekDay;
        if (state.hiddenWeekdays.includes(weekday)) continue;
        items.push(date);
      }
    }
    return items;
  }, [props.startedAt, state.showRange]);

  const frames: Frame[] = useMemo(() => {
    const items = [];
    for (let x = 0; x < dates.length; x++) {
      const date = dates[x];
      const weekday = (date.weekday % 7) as CalendarWeekDay;
      if (state.hiddenWeekdays.includes(weekday)) continue;
      for (let y = 0; y < state.times.length; y++) {
        const time = state.times[y];
        items.push({
          x,
          xSpan: 1,
          y,
          ySpan: 1,
          date: date.set({ hour: time.hour, minute: time.minute }),
        });
      }
    }
    return items;
  }, [dates, state.times]);

  const { state: eventState, actions: eventActions } = useEvent({
    frames,
    startedAt: props.startedAt,
    dates,
  });
  const { state: slotState, actions: slotActions } = useSlot({
    frames,
    startedAt: props.startedAt,
  });

  const selectedGroups = useMemo(() => {
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    return state.selectedGroups.filter((g) => {
      const isOverlap =
        gridStartedAt <= g.endedAt && gridEndedAt >= g.startedAt;
      return isOverlap;
    });
  }, [state.selectedGroups, props.startedAt, state.showRange]);

  const selectedSlots = useMemo(() => {
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    const slots = [];
    for (let index = 0; index < state.selectedSlots.length; index++) {
      const slot = state.selectedSlots[index];
      const startedAt = slot.date;
      const endedAt = slot.date.plus({ minute: state.rulerStep });
      const isOverlap = gridStartedAt <= endedAt && gridEndedAt >= startedAt;
      if (isOverlap) {
        slots.push({
          id: index,
          x: slot.x,
          y: slot.y,
          ySpan: slot.ySpan,
          date: slot.date,
        });
      }
    }
    return slots;
  }, [
    state.selectedSlots,
    frames,
    props.startedAt,
    state.showRange,
    state.rulerStep,
  ]);

  const onClickEvent = (e: SyntheticEvent<HTMLDivElement>) => {
    const { eventId } = e.currentTarget.dataset;
    const event = eventState.events.find((e) => e.uid === eventId);
    if (event && typeof props.onClickEvent === 'function') {
      props.onClickEvent(event, e.currentTarget);
    }
  };

  const addIconUrl = useMemo(() => {
    return isMobileApp()
      ? undefined
      : imagePath('pages/dashboard/calendars/add_slot.svg');
  }, []);

  return {
    state: {
      events: eventState.events,
      dates,
      frames,
      maxRow: state.maxRow,
      rulerStep: state.rulerStep,
      rulerBorderStep: state.rulerBorderStep,
      calendarMode: state.calendarMode,
      selectedGroups,
      selectedSlots,
      draggingEvent: eventState.draggingEvent,
      draggingGhostEvent: eventState.draggingGhostEvent,
      currentDragGroup: slotState.currentDragGroup,
      yStep: slotState.yStep,
      now: state.now,
      isShowSlot: [SlotViewType.SlotAndGroup, SlotViewType.Slot].includes(
        state.slotViewType,
      ),
      isShowGroup: [SlotViewType.SlotAndGroup, SlotViewType.Group].includes(
        state.slotViewType,
      ),
      isLoading: !state.isReady || state.currentPage === 0,
      creatingEvent: state.creatingEvent,
      addIconUrl,
      userColor: state.userColor,
      primaryColor: state.primaryColor,
      dragDirection: eventState.dragDirection,
    },
    actions: {
      onEventDragStart: eventActions.onEventDragStart,
      onEventDragEnter: eventActions.onEventDragEnter,
      onEventDragOver: eventActions.onEventDragOver,
      onEventDrop: eventActions.onEventDrop,
      onEventDragEnd: eventActions.onEventDragEnd,
      onEventDragHandlerMove: eventActions.onEventDragHandlerMove,
      onClickSlot: slotActions.onClickSlot,
      onClickDeleteSlotGroup: slotActions.onClickDeleteSlotGroup,
      onClickAddSlot:
        state.calendarMode === CalendarMode.Event
          ? eventActions.onClickAddSlot
          : slotActions.onClickAddSlot,
      onDayDragStart: slotActions.onDayDragStart,
      onDayDragEnter: slotActions.onDayDragEnter,
      onDayDragEnd: slotActions.onDayDragEnd,
      onClickEvent,
    },
  };
}
